import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Target, FileText, Inbox, Mail, Image, MessageSquare, ClipboardList,
  Clock, FileSpreadsheet, PenLine, Loader2, PencilLine, History, Check, X, ListChecks, Bot, ChevronDown, ChevronRight,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { StatusBadge } from '@/components/common/StatusBadge';
import { Loader, EmptyState } from '@/components/common/Feedback';
import { EventUploadDialog } from '@/components/events/EventUploadDialog';
import { EventReviewDrawer } from '@/components/events/EventReviewDrawer';
import { ForwardEmailCard } from '@/components/projects/ForwardEmailCard';
import { RateTableCard } from '@/components/projects/RateTableCard';
import { ScopeDocumentList } from '@/components/projects/ScopeDocuments';
import { RequirementsTab } from '@/components/projects/RequirementsTab';
import { DocumentChat } from '@/components/projects/DocumentChat';
import { OpportunityDrawer } from '@/components/opportunities/OpportunityDrawer';
import { StrengthBadge, ClassificationBadge, OppStatusBadge, AmountOrStatus } from '@/components/opportunities/OppBadges';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { fmtMoney, fmtDate, SOURCE_LABEL, ANALYSIS_STATUS_META } from '@/lib/format';
import { toast } from 'sonner';

// Feature flag: Rate Card is currently DISABLED (kept in the codebase). Set to
// true to restore the Rate Card management UI.
const RATE_CARD_ENABLED = false;

const SRC_ICON = {
  email: Mail, drawing: PenLine, rfi: FileText, photo: Image, contract: FileText,
  meeting_notes: ClipboardList, meeting_minutes: ClipboardList, site_instruction: FileText,
  whatsapp: MessageSquare, daily_report: FileSpreadsheet, timesheet: Clock, other: FileText,
};

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [opps, setOpps] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewId, setReviewId] = useState(null);
  const [oppId, setOppId] = useState(null);
  const [editScope, setEditScope] = useState(false);
  const [scopeText, setScopeText] = useState('');
  const [savingScope, setSavingScope] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showScopeText, setShowScopeText] = useState(false);
  const [editNotice, setEditNotice] = useState(false);
  const [noticeVal, setNoticeVal] = useState('14');
  const [savingNotice, setSavingNotice] = useState(false);

  const loadEvents = () => api.get(`/events?project_id=${id}`).then((r) => setEvents(r.data)).catch(() => {});
  const loadOpps = () => api.get(`/opportunities?project_id=${id}`).then((r) => setOpps(r.data)).catch(() => {});
  const load = () => Promise.all([
    api.get(`/projects/${id}`).then((r) => { setP(r.data); setScopeText(r.data.original_scope || ''); setNoticeVal(String(r.data.notice_period_days || 14)); }),
    loadEvents(), loadOpps(),
  ]).finally(() => setLoading(false));
  useEffect(() => { load(); }, [id]);

  const saveScope = async () => {
    setSavingScope(true);
    try {
      const r = await api.patch(`/projects/${id}/scope`, { original_scope: scopeText });
      setP({ ...p, original_scope: r.data.original_scope, scope_versions: r.data.scope_versions });
      setEditScope(false);
      toast.success('Scope updated (previous version kept)');
    } catch (e) { toast.error('Could not update scope'); }
    finally { setSavingScope(false); }
  };

  const saveNotice = async () => {
    setSavingNotice(true);
    try {
      const r = await api.patch(`/projects/${id}`, { notice_period_days: parseInt(noticeVal, 10) || 14 });
      setP({ ...p, notice_period_days: r.data.notice_period_days });
      setEditNotice(false);
      toast.success('Notice period updated');
    } catch (e) { toast.error('Could not update notice period'); }
    finally { setSavingNotice(false); }
  };

  if (loading) return <Loader />;
  if (!p) return null;
  const m = p.metrics || {};
  const pricedOpps = opps.filter((o) => o.pricing_status === 'fully_priced').length;
  const scopeDocs = (p.scope_documents || []);
  const hasDocs = scopeDocs.some((d) => d.file_id);
  const hasReadableDocs = scopeDocs.some((d) => d.extraction_ok) || !!(p.original_scope || '').trim();

  return (
    <div className="space-y-5">
      <button onClick={() => navigate('/projects')} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="back-to-projects">
        <ArrowLeft className="h-4 w-4" /> Projects
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">{p.name}</h2>
          <p className="text-sm text-slate-500">{[p.client, p.location, p.contract_type].filter(Boolean).join(' \u00b7 ')} {p.contract_value ? <>&middot; Contract <span className="font-mono font-semibold text-slate-700">{fmtMoney(p.contract_value)}</span></> : null}</p>
        </div>
        <EventUploadDialog projectId={id} onDone={load} trigger={
          <Button className="bg-money hover:bg-money-700" data-testid="add-communication-btn"><Inbox className="h-4 w-4 mr-2" />Add communication</Button>
        } />
      </div>

      {/* Compact PM info row: notice period + opportunities + potential value */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-wide text-slate-400 flex items-center gap-1"><Clock className="h-3 w-3" />Notice period</div>
          {editNotice ? (
            <div className="flex items-center gap-2 mt-1.5">
              <Input className="w-20 h-8" type="number" min="1" value={noticeVal} onChange={(e) => setNoticeVal(e.target.value)} data-testid="notice-input" />
              <Button size="sm" className="h-8 bg-money hover:bg-money-700" disabled={savingNotice} onClick={saveNotice} data-testid="notice-save">
                {savingNotice ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              </Button>
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => { setEditNotice(false); setNoticeVal(String(p.notice_period_days || 14)); }}><X className="h-4 w-4" /></Button>
            </div>
          ) : (
            <div className="flex items-center justify-between mt-1">
              <div className="text-lg font-semibold text-ink-900">{p.notice_period_days || 14} <span className="text-sm font-normal text-slate-400">days</span></div>
              <button onClick={() => setEditNotice(true)} className="text-xs text-brand hover:underline flex items-center gap-1" data-testid="edit-notice-btn"><PencilLine className="h-3.5 w-3.5" />Edit</button>
            </div>
          )}
        </Card>
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-wide text-slate-400 flex items-center gap-1"><Target className="h-3 w-3" />Open opportunities</div>
          <div className="text-lg font-semibold text-brand-600 mt-1" data-testid="proj-open-opps">{m.open_detections || 0}</div>
        </Card>
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-wide text-slate-400">Fully priced</div>
          <div className="text-lg font-semibold font-mono text-money-700 mt-1" data-testid="proj-priced-opps">{pricedOpps}<span className="text-sm font-normal text-slate-400"> / {opps.length}</span></div>
        </Card>
      </div>

      <Card className="p-5">
        <div className="flex items-center justify-between mb-1">
          <div className="text-xs font-semibold text-slate-700">Project Documents &amp; Baseline</div>
          <div className="flex items-center gap-2">
            {p.scope_versions?.length > 1 && (
              <button onClick={() => setShowHistory(true)} className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1" data-testid="scope-history-btn">
                <History className="h-3.5 w-3.5" />{p.scope_versions.length} versions
              </button>
            )}
            {!editScope && (
              <button onClick={() => setEditScope(true)} className="text-xs text-brand hover:underline flex items-center gap-1" data-testid="edit-scope-btn">
                <PencilLine className="h-3.5 w-3.5" />Edit baseline
              </button>
            )}
          </div>
        </div>

        {/* Original uploaded documents — click to view the source PDF */}
        {hasDocs && !editScope && (
          <div className="mb-3">
            <p className="text-[11px] text-slate-400 mb-2">Click a document to view the original file.</p>
            <ScopeDocumentList documents={p.scope_documents} />
          </div>
        )}

        {editScope ? (
          <div className="space-y-2">
            <Textarea rows={5} value={scopeText} onChange={(e) => setScopeText(e.target.value)} data-testid="scope-textarea" />
            <div className="flex gap-2">
              <Button size="sm" className="bg-money hover:bg-money-700 h-8" disabled={savingScope} onClick={saveScope} data-testid="scope-save">
                {savingScope ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5 mr-1" />}Save version
              </Button>
              <Button size="sm" variant="outline" className="h-8" onClick={() => { setEditScope(false); setScopeText(p.original_scope || ''); }}><X className="h-3.5 w-3.5 mr-1" />Cancel</Button>
            </div>
          </div>
        ) : hasDocs ? (
          // Documents present: keep the extracted baseline text tucked away (AI uses it).
          (p.original_scope || '').trim() ? (
            <div className="border-t border-slate-100 pt-2">
              <button onClick={() => setShowScopeText((v) => !v)} className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1" data-testid="toggle-scope-text">
                {showScopeText ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                {showScopeText ? 'Hide' : 'View'} extracted baseline text
              </button>
              {showScopeText && (
                <p className="text-sm text-slate-600 leading-relaxed mt-2 whitespace-pre-wrap max-h-72 overflow-y-auto" data-testid="scope-text">{p.original_scope}</p>
              )}
            </div>
          ) : null
        ) : (
          <p className="text-sm text-slate-600 leading-relaxed" data-testid="scope-text">{p.original_scope || 'No scope summary yet \u2014 add one so Recoup has a baseline to compare against.'}</p>
        )}
      </Card>

      <ForwardEmailCard address={p.inbound_address} onCaptured={load} />

      {/* Rate Card feature is currently DISABLED (kept in the codebase). Flip
          RATE_CARD_ENABLED to true to restore it. */}
      {RATE_CARD_ENABLED && (
        <RateTableCard
          projectId={id}
          projectName={p.name}
          projectCurrency={p.currency || 'USD'}
          hasScope={!!(p.original_scope || '').trim()}
        />
      )}

      <Tabs defaultValue="requirements">
        <TabsList>
          <TabsTrigger value="requirements" data-testid="tab-requirements"><ListChecks className="h-4 w-4 mr-1.5" />Requirements</TabsTrigger>
          <TabsTrigger value="opportunities" data-testid="tab-opportunities"><Target className="h-4 w-4 mr-1.5" />Opportunities ({opps.length})</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-timeline"><Inbox className="h-4 w-4 mr-1.5" />Timeline ({events.length})</TabsTrigger>
          <TabsTrigger value="assistant" data-testid="tab-assistant"><Bot className="h-4 w-4 mr-1.5" />Assistant</TabsTrigger>
          {user?.is_founder && (
            <TabsTrigger value="cos" data-testid="tab-change-orders"><FileText className="h-4 w-4 mr-1.5" />Change Orders ({(p.change_orders || []).length})</TabsTrigger>
          )}
        </TabsList>

        {/* Requirements (auto-extracted from documents) */}
        <TabsContent value="requirements" className="mt-4">
          <RequirementsTab projectId={id} projectName={p?.name} />
        </TabsContent>

        {/* Assistant — chat over the project documents */}
        <TabsContent value="assistant" className="mt-4">
          <DocumentChat projectId={id} hasDocuments={hasReadableDocs} />
        </TabsContent>

        {/* Opportunities */}
        <TabsContent value="opportunities" className="mt-4">
          <Card className="p-0 divide-y divide-slate-100">
            {opps.length === 0 && <EmptyState icon={Target} title="No opportunities yet" description="Add a communication and Recoup will surface anything worth claiming." />}
            {opps.map((o) => {
              const isNew = o.opportunity_status === 'new';
              return (
              <button key={o.id} onClick={() => setOppId(o.id)} data-testid={`proj-opp-${o.id}`}
                className={cn('w-full text-left px-5 py-3.5 flex items-center gap-4 transition-colors hover:bg-slate-50', isNew && 'bg-brand-50/40')}>
                <span className={cn('h-2 w-2 rounded-full shrink-0', isNew ? 'bg-brand' : 'bg-transparent')} data-testid={isNew ? `proj-opp-new-dot-${o.id}` : undefined} aria-label={isNew ? 'New' : undefined} />
                <div className="flex-1 min-w-0">
                  <div className={cn('text-slate-800 text-sm truncate', isNew ? 'font-bold' : 'font-semibold')}>{o.title}</div>
                  <div className={cn('text-xs line-clamp-1', isNew ? 'text-slate-600' : 'text-slate-500')}>{o.summary}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5" data-testid={`proj-opp-detected-${o.id}`}>Detected: {fmtDate(o.created_at)}</div>
                </div>
                <span className="hidden md:inline-flex"><ClassificationBadge classification={o.classification} /></span>
                <StrengthBadge strength={o.strength} confidence={o.confidence} />
                <span className="hidden sm:inline-flex"><OppStatusBadge status={o.opportunity_status} /></span>
                <div className="w-20 sm:w-24 text-right"><AmountOrStatus opportunity={o} compact testid={`proj-opp-amount-${o.id}`} /></div>
              </button>
              );
            })}
          </Card>
        </TabsContent>

        {/* Timeline */}
        <TabsContent value="timeline" className="mt-4">
          <Card className="p-0 divide-y divide-slate-100">
            {events.length === 0 && <EmptyState icon={Inbox} title="No communications yet" description="Add an email, RFI, site instruction or note to get started." />}
            {events.map((e) => {
              const Icon = SRC_ICON[e.source_type] || FileText;
              const meta = ANALYSIS_STATUS_META[e.analysis_status] || ANALYSIS_STATUS_META.pending;
              // Re-label enterprise analysis statuses in plain language for PMs.
              const label = e.analysis_status === 'scope_change' ? 'Opportunity found'
                : e.analysis_status === 'no_change' ? 'Nothing to claim' : meta.label;
              return (
                <button key={e.id} onClick={() => setReviewId(e.id)} data-testid={`event-row-${e.id}`}
                  className="w-full text-left px-5 py-3 flex items-center gap-4 hover:bg-slate-50 transition-colors">
                  <div className="rounded-md bg-slate-100 p-1.5 shrink-0"><Icon className="h-4 w-4 text-slate-600" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-800 text-sm truncate">{e.subject || e.ai_title || e.file_name || 'Communication'}</span>
                      <span className={cn('text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0', meta.cls)}>{label}</span>
                    </div>
                    <div className="text-xs text-slate-500 truncate">{SOURCE_LABEL[e.source_type] || e.source_type} &middot; {e.sender || 'unknown'} &middot; {fmtDate(e.timestamp)}</div>
                  </div>
                </button>
              );
            })}
          </Card>
        </TabsContent>

        {/* Change Orders (founder only) */}
        {user?.is_founder && (
          <TabsContent value="cos" className="mt-4">
            <Card className="p-0 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#FAFBFC] text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  <tr><th className="text-left px-4 py-2.5">CO #</th><th className="text-left px-4 py-2.5">Title</th><th className="text-center px-4 py-2.5">Status</th><th className="text-right px-4 py-2.5">Total</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(p.change_orders || []).map((c) => (
                    <tr key={c.id} className="row-hover cursor-pointer" onClick={() => navigate(`/change-orders/${c.id}`)} data-testid={`proj-co-${c.id}`}>
                      <td className="px-4 py-3 font-mono font-semibold text-slate-700">{c.co_number}</td>
                      <td className="px-4 py-3 text-slate-800">{c.title}</td>
                      <td className="px-4 py-3 text-center"><StatusBadge status={c.status} /></td>
                      <td className="px-4 py-3 text-right font-mono font-semibold">{fmtMoney(c.total)}</td>
                    </tr>
                  ))}
                  {(p.change_orders || []).length === 0 && <tr><td colSpan="4" className="px-4 py-10 text-center text-slate-400">No change orders yet.</td></tr>}
                </tbody>
              </table>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      <EventReviewDrawer eventId={reviewId} open={!!reviewId} onClose={() => setReviewId(null)} onChanged={load} />
      <OpportunityDrawer opportunityId={oppId} open={!!oppId} onClose={() => setOppId(null)} onChanged={load} />

      {/* Scope version history */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-lg" data-testid="scope-history-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><History className="h-4 w-4" />Scope version history</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {[...(p.scope_versions || [])].reverse().map((v, i, arr) => (
              <div key={i} className="rounded-lg border border-slate-200 p-3">
                <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
                  <span>{i === 0 ? 'Current' : `Version ${arr.length - i}`} &middot; {v.by}</span><span>{fmtDate(v.at)}</span>
                </div>
                <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{v.text}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
